import { useCallback } from 'react';
import { log } from 'log';
import { parseMessage, sendMessage } from '../utils/messageUtils';
import { useGameHandler } from './useGameHandler';

import * as Types from '../types';

export const useMessageHandler = ({
    subscribedFeatures = '',
    iframeClass = 'game-iframe'
}: Types.IConfig) => {
    const { handleGameLaunch } = useGameHandler();

    const handleInitializationRequest = useCallback((features: string[] = []) => {
        const additionalFeatures = subscribedFeatures
            .split(',')
            .map((feature) => feature.replace(/['"]/g, '').trim());
        const filterCriteria = ['displaystatus', ...additionalFeatures];

        sendMessage(
            {
                _type: 'ucip.basic.w2gInitializationResponse',
                version: '1.0.0',
                features: features.filter((feature: string) => filterCriteria.includes(feature))
            },
            iframeClass
        );
    }, [subscribedFeatures, iframeClass]);

    const handleCloseGameCommand = useCallback(() => {
        log('info', '[UCIP]: Close game');
        if (window.opener) {
            // close current browser window
            window.close();
        } else {
            // return to the home page
            const url = new URL(location.href);
            url.searchParams.forEach((value, name, params) => params.delete(name));
            url.pathname = '';
            location.href = url.toString();
        }
    }, []);

    const processMessage = useCallback((message: Types.IMessage | null) => {
        if (!message) {
            log('warn', '[UCIP]: Invalid message format');
            return;
        }

        const { _type: type, features = [], gameCode, options } = message;

        switch (type) {
            case 'ucip.basic.g2wInitializationRequest':
                handleInitializationRequest(features);
                break;

            case 'ucip.basic.g2wCloseGameFrameCommand':
                handleCloseGameCommand();
                break;

            case 'ucip.switchgame.g2wSwitchGameCommand':
            case 'ucip.gamechange.g2wGameChangeNotification':
                log('info', `[UCIP]: ${type} command received`);
                handleGameLaunch(gameCode, options?.launchAlias);
                break;

            default:
                break;
        }
    }, [handleInitializationRequest, handleCloseGameCommand, handleGameLaunch]);

    const onMessage = useCallback((event: MessageEvent) => {
        log('info', '[UCIP]: ', event);
        const message = parseMessage(event);
        processMessage(message);
    }, [processMessage]);

    return { onMessage };
};
